import type { Allocation } from '@/sections/visual-strategy/types';
import { formatNumber as formatSharedNumber } from '@/utils/number';

export const DONUT_COLORS = [
  '#07314C',
  '#115A8A',
  '#3681B2',
  '#6AA9D2',
  '#DFF2FF',
];

export const BAR_COLORS = {
  positive: '#11825A',
  negative: '#D82455',
};

export const formatPercent = (
  value: number,
  { signed = true }: { signed?: boolean } = {},
) => {
  const sign = signed && value > 0 ? '+' : '';
  return `${sign}${formatSharedNumber(value, '', 1).replace(/\\.0$/, '')}%`;
};

/**
 * Timestamp for the start of the calendar year of the given ISO date, used as
 * the chart x-axis lower bound so the first year always renders a tick.
 *
 * Chart.js derives year ticks via the date adapter's local-time start-of-year.
 * Anchoring `min` to an exact UTC Jan 1 lets that first tick drift just before
 * `min` in UTC+ timezones (e.g. CET) and get filtered out.
 * Flooring to the local start of year keeps the first year's tick on the axis.
 * The year is read from the ISO string rather than via local Date methods,
 * so it stays timezone-independent.
 */
export const getFirstYearStartMs = (isoDate?: string) => {
  if (!isoDate) {
    return undefined;
  }

  const yearMatch = /^(\d{4})/.exec(isoDate);
  if (yearMatch) {
    return new Date(Number(yearMatch[1]), 0, 1).getTime();
  }

  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : new Date(parsed.getFullYear(), 0, 1).getTime();
};

export const formatValue = (value: number) =>
  formatSharedNumber(value, '', 1).replace(/\\.0$/, '');

export const formatAsOfDate = (value?: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('de-CH').format(date);
};

export const allocationLabel = (allocation: Allocation) =>
  allocation.currency || allocation.assetClass || allocation.label || '-';

export const getConicGradient = (allocations: Allocation[]) => {
  if (!allocations.length) {
    return '#115A8A';
  }

  let current = 0;
  const segments = allocations.map((allocation, index) => {
    const next = current + allocation.percentage;
    const color = DONUT_COLORS[index % DONUT_COLORS.length];
    const segment = `${color} ${current}% ${next}%`;
    current = next;
    return segment;
  });

  return `conic-gradient(${segments.join(', ')})`;
};
